// src/services/companyCreditService.js

import axiosInstance from '../config/axios.config';

class CompanyCreditService {
    /**
     * Get company admin's current credit balance
     * @returns {Promise} API response with balance data
     */
    async getBalance() {
        try {
            const response = await axiosInstance.get('/company/credits/balance');
            return response.data;
        } catch (error) {
            console.error('Error fetching credit balance:', error);
            throw error;
        }
    }

    /**
     * Get company employees with their credit balances (with server-side pagination)
     * @param {Object} params - Query parameters (page, limit, search, sort_field, sort_order)
     * @returns {Promise} API response with paginated employee list and balances
     */
    async getEmployeesCreditBalances(params = {}) {
        try {
            const response = await axiosInstance.get('/company/credits/employees/balances', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching employees credit balances:', error);
            throw error;
        }
    }

    /**
     * Transfer credits to an employee
     * @param {number} employeeUserId - Employee user ID
     * @param {number} credits - Amount of credits to transfer
     * @param {string} reason - Reason for transfer (optional)
     * @param {string} note - Additional note (optional)
     * @returns {Promise} API response with transfer result
     */
    async transferCredits(employeeUserId, credits, reason = null, note = null) {
        try {
            const response = await axiosInstance.post('/company/credits/transfer', {
                employee_user_id: employeeUserId,
                credits: credits,
                reason: reason,
                note: note
            });
            return response.data;
        } catch (error) {
            console.error('Error transferring credits:', error);
            throw error;
        }
    }

    /**
     * Reclaim credits from an employee
     * @param {number} employeeUserId - Employee user ID
     * @param {number} credits - Amount of credits to reclaim
     * @param {string} reason - Reason for reclaiming (REQUIRED)
     * @param {string} note - Additional note (optional)
     * @returns {Promise} API response with reclaim result
     */
    async reclaimCredits(employeeUserId, credits, reason, note = null) {
        try {
            const response = await axiosInstance.post('/company/credits/reclaim', {
                employee_user_id: employeeUserId,
                credits: credits,
                reason: reason,
                note: note
            });
            return response.data;
        } catch (error) {
            console.error('Error reclaiming credits:', error);
            throw error;
        }
    }

    /**
     * Get transfer history with filters
     * @param {Object} params - Query parameters (page, per_page, transfer_type, date_from, date_to)
     * @returns {Promise} API response with paginated history
     */
    async getTransferHistory(params = {}) {
        try {
            const response = await axiosInstance.get('/company/credits/history', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching transfer history:', error);
            throw error;
        }
    }

    /**
     * Get specific employee's complete transaction history
     * @param {number} employeeId - Employee user ID
     * @param {Object} params - Query parameters (page, limit, type, date_from, date_to)
     * @returns {Promise} API response with paginated transactions
     */
    async getEmployeeTransactions(employeeId, params = {}) {
        try {
            const response = await axiosInstance.get(`/company/credits/employees/${employeeId}/transactions`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching employee transactions:', error);
            throw error;
        }
    }

    /**
     * Update employee user details
     * @param {number} userId - Employee user ID
     * @param {Object} data - User details (name, email, first_name, last_name, mobile_number)
     * @returns {Promise} API response with update result
     */
    async updateEmployeeDetails(userId, data) {
        try {
            const response = await axiosInstance.put(`/company/credits/employees/${userId}/details`, data);
            return response.data;
        } catch (error) {
            console.error('Error updating employee details:', error);
            throw error;
        }
    }
}

export default new CompanyCreditService();
